import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import {
  canMarkPersonalExpense,
  canRepayPersonalExpense,
  canRevertPersonalExpense,
  canSeePersonalExpenseActions,
} from "utils/transactions/transactionActionGuards";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, TextField, toast } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import Role from "enums/Role";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import PersonalBankAccountDrawer from "components/common/users/PersonalBankAccountDrawer";
import { useMutation } from "@apollo/client";
import { titleCase } from "title-case";
import {
  CANCEL_PERSONAL_EXPENSE,
  MARK_TRANSACTION_AS_PERSONAL_EXPENSE,
  PAY_PERSONAL_EXPENSE,
} from "graphql/transaction";
import { useFeatureEntitlements } from "@frontegg/react";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetPersonalBankAccount from "hooks/useGetPersonalBankAccount";

interface PersonalExpenseProps {
  transaction: any;
  setLoadingPersonalExpense: (loading: boolean) => void;
  onStatusChange?: () => void;
}

const PersonalExpenseButtons: React.FC<PersonalExpenseProps> = ({
  transaction,
  setLoadingPersonalExpense,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featurePersonalExpense } = useFeatureEntitlements(
    "feature_personal_expense",
  );
  const { user } = useGetLoggedInUser();
  const [personalExpenseComment, setPersonalExpenseComment] = useState("");

  const [showBankAccountDrawer, setShowBankAccountDrawer] = useState(false);
  const [openPersonalExpensePopup, setOpenPersonalExpensePopup] =
    useState(false);
  const [openRepayPopup, setOpenRepayPopup] = useState(false);
  const [openCancelPersonalExpensePopup, setOpenCancelPersonalExpensePopup] =
    useState(false);

  const isManagerOrHigher = useCheckUserRole(Role.Manager);

  const transactionOwner = user?.id === transaction?.user?.id;
  const isNonOwnerMarkingPersonal = !transactionOwner && isManagerOrHigher;

  const viewer = {
    isOwner: transactionOwner,
    isManager: isManagerOrHigher,
    personalExpenseEnabled: featurePersonalExpense,
  };
  const showMarkAsPersonalExpenseButton = canMarkPersonalExpense(
    transaction,
    viewer,
  );
  const showRepayPersonalExpenseButton = canRepayPersonalExpense(
    transaction,
    viewer,
  );
  const showCancelPersonalExpenseButton = canRevertPersonalExpense(
    transaction,
    viewer,
  );

  // The repayment goes to the card's own program, which can only debit a bank linked through its issuer.
  const program = transaction?.card?.cardProgram ?? undefined;
  const {
    personalBankAccount,
    loading: loadingBank,
    refetch: refetchBankAccount,
  } = useGetPersonalBankAccount({
    skip: !showRepayPersonalExpenseButton,
    programId: program?.id,
  });

  const [markTransactionAsPersonalExpense, { loading: loadingMarkAsPersonal }] =
    useMutation(MARK_TRANSACTION_AS_PERSONAL_EXPENSE, {
      variables: {
        transactionId: transaction.id,
        personalExpenseComment: personalExpenseComment,
      },
    });

  const [payPersonalExpense, { loading: loadingPayPersonal }] = useMutation(
    PAY_PERSONAL_EXPENSE,
    {
      variables: { transactionId: transaction.id },
    },
  );

  const [cancelPersonalExpense, { loading: loadingCancelPersonal }] =
    useMutation(CANCEL_PERSONAL_EXPENSE, {
      variables: { transactionId: transaction.id },
    });

  useEffect(() => {
    setLoadingPersonalExpense(
      loadingMarkAsPersonal || loadingPayPersonal || loadingCancelPersonal,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMarkAsPersonal, loadingPayPersonal, loadingCancelPersonal]);

  useEffect(() => {
    setPersonalExpenseComment("");
  }, [openPersonalExpensePopup]);

  // Handler functions with try/catch
  const handleMarkPersonalExpense = async () => {
    try {
      await markTransactionAsPersonalExpense();
      toast.success(t("Marked as personal expense"));
      setOpenPersonalExpensePopup(false);
      onStatusChange && onStatusChange();
    } catch (error: any) {
      toast.error(error.message || t("Failed to mark as personal expense"));
    }
  };

  const handleCancelPersonalExpense = async () => {
    try {
      await cancelPersonalExpense();
      toast.success(t("Marked as business expense"));
      setOpenCancelPersonalExpensePopup(false);
      onStatusChange && onStatusChange();
    } catch (error: any) {
      toast.error(error.message || t("Failed to mark as business expense"));
    }
  };

  const handlePayPersonalExpense = async () => {
    try {
      await payPersonalExpense();
      toast.success(t("Repayment started"));
      setOpenRepayPopup(false);
      onStatusChange && onStatusChange();
    } catch (error: any) {
      toast.error(error.message || t("Failed to start repayment"));
    }
  };

  if (
    !canSeePersonalExpenseActions(transaction, viewer) &&
    !showRepayPersonalExpenseButton &&
    !showCancelPersonalExpenseButton
  ) {
    return null;
  }

  return (
    <>
      <Inline justify="end" gap="var(--space-2)">
        {showMarkAsPersonalExpenseButton && (
          <Button
            variant="default-outlined"
            size="small"
            onClick={() => setOpenPersonalExpensePopup(true)}
          >
            {t("Mark Personal")}
          </Button>
        )}

        {showRepayPersonalExpenseButton && (
          <Button
            variant="secondary-filled"
            size="small"
            onClick={() => setOpenRepayPopup(true)}
          >
            {t("Repay")}
          </Button>
        )}

        {showCancelPersonalExpenseButton && (
          <Button
            variant="danger-outlined"
            size="small"
            onClick={() => setOpenCancelPersonalExpensePopup(true)}
          >
            {t("Mark Business")}
          </Button>
        )}
      </Inline>

      <ConfirmDialog
        open={openPersonalExpensePopup}
        title={t("Mark as Personal Expense")}
        message={
          <Stack gap="var(--space-4)">
            <Typography variant="body2" component="span">
              {isNonOwnerMarkingPersonal
                ? t("MarkPersonalDescriptionAdmin")
                : t("MarkPersonalDescriptionOwner")}
            </Typography>
            {isNonOwnerMarkingPersonal && (
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={4}
                required
                label={t("Required Notes for Personal Expense")}
                placeholder={t("Why is this a personal expense?")}
                name="notes"
                value={personalExpenseComment}
                onChange={(e) => setPersonalExpenseComment(e.target.value)}
              />
            )}
          </Stack>
        }
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        tone="primary"
        busy={loadingMarkAsPersonal}
        // A missing note never resolves on its own, so it disables rather than
        // spins (the OUT-1110 split between busy and confirmDisabled).
        confirmDisabled={!personalExpenseComment && isNonOwnerMarkingPersonal}
        onConfirm={handleMarkPersonalExpense}
        onCancel={() => setOpenPersonalExpensePopup(false)}
      />

      <ConfirmDialog
        open={openCancelPersonalExpensePopup}
        title={t("Mark as Business Expense")}
        message={t("CancelPersonalExpenseDescription")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        tone="primary"
        busy={loadingCancelPersonal}
        onConfirm={handleCancelPersonalExpense}
        onCancel={() => setOpenCancelPersonalExpensePopup(false)}
      />

      <ConfirmDialog
        open={openRepayPopup}
        title={t("Repay Personal Expense")}
        message={
          <Stack gap="var(--space-3)">
            <Typography variant="body2" component="span">
              {t("PersonalRepaymentDescription")}
            </Typography>
            {loadingBank ? (
              <Typography variant="body2" component="span">
                {t("Loading personal bank account")}
              </Typography>
            ) : (
              <Inline gap="var(--space-4)" justify="between">
                <Stack gap="var(--space-1)">
                  <Typography variant="label" component="span">
                    {t("Personal Bank Account")}:
                  </Typography>
                  <Typography variant="body2" component="span">
                    {personalBankAccount?.bankName
                      ? `${titleCase(personalBankAccount?.bankName.toLowerCase())} ••••${personalBankAccount?.last4}`
                      : t("PersonalBankAccountNotLinkedForCardProgram")}
                  </Typography>
                </Stack>
                <Button
                  variant="primary-outlined"
                  size="small"
                  onClick={() => {
                    setShowBankAccountDrawer(true);
                    setOpenRepayPopup(false);
                  }}
                >
                  {personalBankAccount ? t("Edit") : t("Add")}
                </Button>
              </Inline>
            )}
          </Stack>
        }
        confirmLabel={t("Start Repayment")}
        cancelLabel={t("Cancel")}
        tone="primary"
        busy={loadingPayPersonal}
        // No linked account is a precondition the user must go and fix, so it
        // disables rather than spinning. `loadingBank` is excluded: it resolves
        // on its own and the row above already says it is loading.
        confirmDisabled={!personalBankAccount && !loadingBank}
        onConfirm={handlePayPersonalExpense}
        onCancel={() => setOpenRepayPopup(false)}
      />

      <PersonalBankAccountDrawer
        open={showBankAccountDrawer}
        program={program}
        handleCloseDrawer={() => {
          setShowBankAccountDrawer(false);
          refetchBankAccount();
        }}
      />
    </>
  );
};

export default PersonalExpenseButtons;
